import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '../../../utils/apolloClient';
import { environmentURLs } from '../../../components/common/CaptureEnvironment';

const initialState = {
  availableLocations: [],
  optOutsByLocation: {},
  loadingOptOuts: false,
  loadingOptOutsError: null,
  updateCppPending: false,
  updateCppError: null,
  updateCppSuccess: false,
  solutionInfo: {},
  fetchingSolution: false,
  getSolutionError: null
};

function buildOptOutsFromCppEntries(entries, locations, apiDetails) {
  return entries
    .reduce((acc, cppEntry) => {
      // providerId is a bare rest or async api id with no api-v2- prefix.
      // check the app's integrations to see if we need to add the prefix.
      // (we do for rest, but not async)
      const apiId = apiDetails?.some(api => api.id === `api-v2-${cppEntry.providerId}`)
        ? `api-v2-${cppEntry.providerId}`
        : cppEntry.providerId;
      const newOptOuts = {
        ...acc[cppEntry.entityId],
        [apiId]: cppEntry.cppFlag
      };

      return {
        ...acc,
        [cppEntry.entityId]: newOptOuts
      };
    }, {});
}

export const getCppForAppAndLocations = createAsyncThunk(
  'getCppForAppAndLocations',
  async ({ appId, locations, apiDetails, token }) => {
    const { data } = await client.authorizeRequest({
      method: 'POST',
      url: `${environmentURLs.REACT_APP_PRIVACY_PROTECTION_URL}/getByEntities`,
      data: {
        consumerId: appId,
        entityIds: locations.map(loc => loc.entityId)
      },
      headers: {
        Authorization: `Bearer ${token}`
      },
    })
      .catch(e => {
        if (e.response?.status === 404) {
          return { data: [] };
        }
        throw e;
      });
    return { data, locations, apiDetails };
  }
)

export const updateCppForApp = createAsyncThunk(
  'updateCppForApp',
  async ({ appId, optOuts, locations, cppSupportedApis, token }) => {
    const apiOptOutsBase = cppSupportedApis.reduce((acc, apiId) => ({
      ...acc,
      [apiId]: false
    }), {});

    const optOutsGapsFilled = locations.reduce((acc, loc) => ({
      ...acc,
      [loc.entityId]: apiOptOutsBase
    }), {});
    for (const [orgId, apiOptOuts] of Object.entries(optOuts)) {
      optOutsGapsFilled[orgId] = { ...apiOptOutsBase, ...apiOptOuts };
    }

    const { data } = await client.authorizeRequest({
      method: 'PATCH',
      url: `${environmentURLs.REACT_APP_MP_DP_METADATA_URL}/privacy-protection`,
      data: { appId, optOuts: optOutsGapsFilled },
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    return { data };
  }
)

export const getSolution = createAsyncThunk(
  'getSolution',
  async ({solutionId, token}) => {

    const { data } = await client.authorizeRequest({
      method: 'GET',
      url: `${environmentURLs.REACT_APP_MP_DP_METADATA_URL}/solutions/${solutionId}`,
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    return { data };
  }
)

export const privacyProtectionSlice = createSlice({
  name: 'privacyProtectionSlice',
  initialState,
  reducers: {
    setCppAvailableLocations: (state, action) => {
      state.availableLocations = action.payload;
      for (const orgId of Object.keys(state.optOutsByLocation)) {
        if (!state.availableLocations.some(loc => loc.entityId === orgId)) {
          delete state.optOutsByLocation[orgId];
        }
      }
    },
    toggleCppForLocationsAndApis: (state, action) => {
      const { orgIds, apiIds } = action.payload;

      let destinationState = true;
      if (orgIds.every(
        orgId => apiIds.every(apiId => state.optOutsByLocation[orgId]?.[apiId])
      )) {
        // all requested location/api combinations are selected, deselect them all
        destinationState = false;
      }

      orgIds.forEach(orgId => {
        apiIds.forEach(apiId => {
          if (state.optOutsByLocation[orgId]) {
            state.optOutsByLocation[orgId][apiId] = destinationState;
          } else {
            state.optOutsByLocation[orgId] = { [apiId]: destinationState };
          }
        });
      });
    },
    clearCppSelections: state => {
      state.optOutsByLocation = {};
    },
    clearCppUpdateError: state => {
      state.updateCppError = null;
    }
  },
  extraReducers: {
    [getCppForAppAndLocations.pending]: state => {
      state.optOutsByLocation = {};
      state.loadingOptOutsError = null;
      state.loadingOptOuts = true;
    },
    [getCppForAppAndLocations.fulfilled]: (state, { payload }) => {
      const { data, locations, apiDetails } = payload;

      state.loadingOptOuts = false;
      state.availableLocations = locations;
      state.optOutsByLocation = buildOptOutsFromCppEntries(data, locations, apiDetails);
    },
    [getCppForAppAndLocations.rejected]: (state, { error }) => {
      state.loadingOptOuts = false;
      state.loadingOptOutsError = error;
    },
    [updateCppForApp.pending]: state => {
      state.updateCppPending = true;
      state.updateCppError = null;
      state.updateCppSuccess = false;
    },
    [updateCppForApp.fulfilled]: state => {
      state.updateCppPending = false;
      state.updateCppSuccess = true;
    },
    [updateCppForApp.rejected]: (state, { error }) => {
      state.updateCppPending = false;
      state.updateCppError = error;
      state.updateCppSuccess = false;
    },
    [getSolution.pending]: state => {
      state.fetchingSolution = true;
    },
    [getSolution.fulfilled]: (state,{payload}) => {
      state.fetchingSolution = false;
      state.solutionInfo = payload.data;
    },
    [getSolution.rejected]: (state, { error }) => {
      state.fetchingSolution = false;
      state.getSolutionError = error;
    },
  }
});

export const {
  setCppAvailableLocations,
  toggleCppForLocationsAndApis,
  clearCppSelections,
  clearCppUpdateError
} = privacyProtectionSlice.actions;

export default privacyProtectionSlice.reducer;
